import { useMemo } from "react";
import { Stack, Typography } from "@altimateai/lego";
import dayjs from "dayjs";
import { DateRange } from "@/components/dateRange";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { useBreadcrumb } from "@/components/breadcrumbs";
import { ComputeTabPanel } from "./components";
import type { Granularity } from "./components/ClusterCostTrends";
import { getDaysBetween } from "@/helpers/utils";

const FILTER_PORTAL_ID = "clusters-filter-container";
const SAVED_FILTERS_PORTAL_ID = "clusters-saved-filters-container";

const Clusters = () => {
  useBreadcrumb("Clusters");
  const { searchParams, updateSearchParamsByKey } = useFiltersBySearchParams();

  const { startDate, endDate } = useDateFromSearchParams(
    dayjs().subtract(28, "day").toDate(),
    dayjs().toDate()
  );

  const days = useMemo(() => getDaysBetween(startDate, endDate) + 1, [startDate, endDate]);

  const rawGranularity = searchParams.get("granularity");
  const granularity: Granularity =
    rawGranularity === "week" || rawGranularity === "month" ? rawGranularity : "day";

  const handleGranularityChange = (value: string) => {
    updateSearchParamsByKey({ granularity: value });
  };

  return (
    <Stack direction={"column"} gap={"xl"}>
      <Stack justify={"between"} align={"center"}>
        <Typography variant={"page-heading"}>Clusters</Typography>
        <Stack direction={"row"} align={"center"} gap={"md"}>
          <DateRange
            startDate={startDate}
            endDate={endDate}
            availableStaticRanges={[
              "Last 7 days",
              "Last 28 days",
              "Last 3 months",
              "Last 6 months",
              "Last 12 months",
            ]}
            onDateRangeSelect={(newStartDate, newEndDate) => {
              updateSearchParamsByKey({
                start_date: newStartDate?.toISOString(),
                end_date: newEndDate?.toISOString(),
              });
            }}
          />
        </Stack>
      </Stack>

      <ComputeTabPanel
        filterPortalId={FILTER_PORTAL_ID}
        savedFiltersPortalId={SAVED_FILTERS_PORTAL_ID}
        granularity={granularity}
        days={days}
        onGranularityChange={handleGranularityChange}
      />
    </Stack>
  );
};

export default Clusters;
